import { useContext, useEffect, useState } from "react";
import './GroupsPage.css';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Button, IconButton, LinearProgress, Paper, TablePagination, Tooltip, useTheme } from "@mui/material";
import { Group } from "../interfaces/APIInterface";
import { COSMOS_API, POST_ENDPOINT } from "../api/API";
import { AdminContext } from "../contexts/AdminContext";
import TableDisplayComponent from "../components/TableDisplay";
import AddGroupMembers from "../components/AddGroupMembers";
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { RoleObject } from "../interfaces/ApplicationInterfaces";

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
}
const GroupsPage = (props: any) => {
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [selectedGroupData, setSelectedGroupData] = useState<Group | undefined>(undefined);
  const [selectedAllMembers, setSelectedAllMembers] = useState<Array<string>>([]);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState<Array<string>>([]);
  const [openAddMembersPopup, setOpenAddMembersPopup] = useState<boolean>(false);
  const [groupsAvailable, setGroupsAvailable] = useState<Array<string>>([]);
  const [globalRole, setGlobalRole] = useState<boolean>(false);
  const [fetchGroupDataInProgress, setFetchGroupDataInProgress] = useState<boolean>(false);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const { adminCredentials, getToken } = useContext(AdminContext);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const theme = useTheme();

  useEffect(() => {
    console.log("adminCredentials :", adminCredentials);
    let _groupsList: Array<string> = [];
    if(!adminCredentials.roles){
      setGroupsAvailable([]);
      return;
    }
    adminCredentials.roles.forEach((role: RoleObject) => {
      if (role['scope']['type'] == "Group") {
        _groupsList.push(role['scope']['objectId']);
      } else if (role['scope']['type'] == "Global") {
        setGlobalRole(true);
      }
    })
    setGroupsAvailable(_groupsList);
  }, [adminCredentials]);

  const resetGroupPaging = () => {
    setPage(0);
    setNextToken(null);
  }

  const handleAddMembersToGroup = (members: Array<string>) => {
    COSMOS_API.POST({ memberIds: members, groupId: selectedGroupId }, POST_ENDPOINT.AddGroupMembers, getToken())
      .then((res) => {
        if (res.ok) {
          let results = res.respBody;
          if (!results) {
            enqueueSnackbar(`Failed to add any users to group`, { variant: 'error' });
          } else {
            if (Object.keys(results).every(usrId => results[usrId]?.status == "Added")) {
              enqueueSnackbar(
                'All users successfully added to group',
                { variant: 'success' }
              )
            } else {
              let failed = Object.keys(results).filter(usrId => results[usrId]?.status == "Failed").length;
              if (failed > 0) {
                enqueueSnackbar(`Failed to add ${failed} users to group`, { variant: 'error' });
              } else {
                let added = Object.keys(results).filter(usrId => results[usrId]?.status == "Added").length;
                let requested = Object.keys(results).filter(usrId => results[usrId]?.status == "Requested").length;
                let alreadyAdded = Object.keys(results).filter(usrId => results[usrId]?.status == "AlreadyAdded").length;
                let alreadyRequested = Object.keys(results).filter(usrId => results[usrId]?.status == "AlreadyRequested").length;
                let msgs = [];
                if (added > 0) {
                  msgs.push(`Added ${added} users to group`);
                }
                if (requested > 0) {
                  msgs.push(`Requested ${requested} users for group`);
                }
                if (alreadyAdded > 0) {
                  msgs.push(`${alreadyAdded} users are already part of group`);
                }
                if (alreadyRequested > 0) {
                  msgs.push(`${alreadyRequested} users are already pending approval`);
                }
                enqueueSnackbar(msgs.join('; '), { variant: 'success' });
              }
            }
          }
          setSelectedAllMembers([]);
          resetGroupPaging();
          selectedGroupId && fetchGroupData(selectedGroupId, null);
          
        } else {
          enqueueSnackbar(
            `Error: ${res.respBody.errorMessage ?? ''
            }`,
            { variant: 'error' }
          );
        }
        setOpenAddMembersPopup(false);
      })
  }

  const handleRemoveMembersFromGroup = (members: Array<string>) => {
    console.log("remove group members :", members);

    COSMOS_API.POST({ memberIds: members, groupId: selectedGroupId }, POST_ENDPOINT.RemoveGroupMembers, getToken())
      .then((res) => {
        if (res.ok) {
          setSelectedAllMembers([]);
          resetGroupPaging();
          selectedGroupId && fetchGroupData(selectedGroupId, null);
          setSelectedGroupMembers([]);
        } else {
          enqueueSnackbar(
            `Error: ${res.respBody.errorMessage ?? ''
            }`,
            { variant: 'error' }
          );
        }
      })
  }


  const fetchGroupData = (groupId: string, nextToken: string | null) => {
    setFetchGroupDataInProgress(true);
    COSMOS_API.POST({ id: groupId, includeMembers: true, nextToken: nextToken }, POST_ENDPOINT.GetGroupData, getToken())
      .then((res) => {
        if (res.ok) {
          if (nextToken) {
            setSelectedGroupData({
              ...res.respBody,
              members: [
                ...(selectedGroupData?.members ?? []),
                ...res.respBody.members
              ]
            });
            setNextToken(res.respBody.nextToken ?? null);
          } else {
            setSelectedGroupData({
              ...res.respBody
            });
            setNextToken(res.respBody.nextToken ?? null);
          }
          setFetchGroupDataInProgress(false);
        } else {
          enqueueSnackbar(
            `Error: ${res.respBody.errorMessage ?? ''
            }`,
            { variant: 'error' }
          );
          setFetchGroupDataInProgress(false);
        }
      })
  }

  const handleGroupChange = (event: SelectChangeEvent) => {
    let groupId = event.target.value;
    if (groupId != "") {
      setSelectedGroupId(groupId);
      resetGroupPaging();
      fetchGroupData(groupId, null);
    }
  };

  const handleAddMembers = () => {
    setOpenAddMembersPopup(true);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (((selectedGroupData?.members?.length ?? 0) <= ((newPage + 1) * rowsPerPage)) && (nextToken)) {
      fetchGroupData(selectedGroupId, nextToken);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (((selectedGroupData?.members?.length ?? 0) <= rowsPerPage) && (nextToken)) {
      fetchGroupData(selectedGroupId, nextToken);
    }
  };

  const columns: HeadCell[] = [
    {
      id: 'id',
      numeric: false,
      disablePadding: true,
      label: 'User Id',
    },
    {
      id: 'email',
      numeric: false,
      disablePadding: false,
      label: 'Email',
    }
  ];

  return (
    <Paper
      component={"main"}
      className='off-white'
      sx={{ backgroundColor: theme.palette.background.default }}
    >
      <>
        <div className="container">
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                Select a group to view members and group information.
                <br /><br />
                <Box sx={{ minWidth: 300 }}>
                  <FormControl fullWidth>
                    <InputLabel id="group-label">Group</InputLabel>
                    <Select
                      labelId="group-label"
                      id="group-select"
                      value={selectedGroupId || ""}
                      label="Group"
                      onChange={handleGroupChange}
                    >
                      {adminCredentials.allGroups && Object.keys(adminCredentials.allGroups).length > 0 && Object.keys(adminCredentials.allGroups)
                        .filter((groupId: string) => (adminCredentials.isSuperAdmin || globalRole || groupsAvailable.includes(groupId)))
                        .map((groupId: string) =>
                          <MenuItem key={groupId} value={groupId}>{adminCredentials.allGroups[groupId]["displayName"]}</MenuItem>
                        )}
                    </Select>
                  </FormControl>
                </Box>
                {selectedGroupData && <Button variant="contained" onClick={handleAddMembers}>Search/Add Members</Button>}
              </Box>
              <Box id="groupInfoWrapper">
                {!selectedGroupData && <>Select a group to see its information.</>}
                {selectedGroupData && <>
                  <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '20px' }}>
                    <Box className="groupInfo">
                      <span className="infoLabel">Id :</span> {selectedGroupData.id}
                    </Box>
                    <Box className="groupInfo">
                      <Box className="infoLabel">Display Name : </Box>
                      {selectedGroupData.displayName}
                    </Box>
                    <Box className="groupInfo">
                      <Box className="infoLabel">Description :</Box>
                      {selectedGroupData.description}
                    </Box>
                    <Box className="groupInfo">
                      <Box className="infoLabel">Admin Writable : </Box>
                      {selectedGroupData.adminWritable != true ? "No" : "Yes"}
                    </Box>
                  </Box>
                  {openAddMembersPopup && <AddGroupMembers openAddMembersPopup={openAddMembersPopup} groupMembers={selectedGroupData.members} handleAddMembersToGroup={handleAddMembersToGroup} groupId={selectedGroupId} adminWritable={selectedGroupData.adminWritable} handleClose={() => { setOpenAddMembersPopup(false) }} />}
                </>}
              </Box>
            </Box>
            {fetchGroupDataInProgress &&
              <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                Fetching member details for the group...
                <LinearProgress/>
              </Box>}
            {selectedGroupData && <Box id="groupMemberInfo" >
              {!fetchGroupDataInProgress && selectedGroupData.members.length == 0 && <>No users in this group.</>}
              {!fetchGroupDataInProgress && selectedGroupData.members.length > 0 &&
                <Box sx={{ width: '100%' }}>
                  <Paper sx={{ width: '100%', mb: 2 }}>
                    <TableDisplayComponent
                      members={selectedGroupData.members.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                      selectedMembers={selectedGroupMembers}
                      handleAction={handleRemoveMembersFromGroup}
                      columns={columns}
                      tableTitle="Group Members"
                      actionIcon={<Tooltip title="Remove Members" >
                        <IconButton>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      enableAction={selectedGroupData.adminWritable}
                    />
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={(!!nextToken) ? -1 : (selectedGroupData?.members?.length ?? -1)}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      slotProps={{
                        actions: {
                          nextButton: {
                            disabled: (!nextToken) && (((page+1)*rowsPerPage) >= selectedGroupData?.members?.length)
                          }
                        }
                      }}
                      labelDisplayedRows={({from, to, count, page}) => `${from}-${to} of ${(!!nextToken) ? 'more than ' : (selectedGroupData?.members ? '' : 'more than ')}${selectedGroupData?.members?.length ?? to}`}
                    />
                  </Paper></Box>

              }
            </Box>}
          </Box>
        </div>
      </>
    </Paper>
  )
}

export default GroupsPage